<template>
    <div class="chat-editor" @keydown="catchEvents">
        <bubble-menu :editor="editor" :tippy-options="tippyOptions" v-if="editor" :class="'editable-tippy'">
            <div class="bubble-menu">
                <div
                    v-if="!isText"
                    class="bubble-menu-item"
                    @click="editor.chain().focus().toggleBold().run()"
                    :class="{ 'is-active': editor.isActive('bold') }"
                >
                    <div class="material-icons">format_bold</div>
                </div>
                <div
                    v-if="!isText"
                    class="bubble-menu-item"
                    @click="editor.chain().focus().toggleItalic().run()"
                    :class="{ 'is-active': editor.isActive('italic') }"
                >
                    <div class="material-icons">format_italic</div>
                </div>
                <div
                    v-if="!isText"
                    class="bubble-menu-item"
                    @click="editor.chain().focus().toggleStrike().run()"
                    :class="{ 'is-active': editor.isActive('strike') }"
                >
                    <div class="material-icons">strikethrough_s</div>
                </div>
                <div
                    v-if="!isText"
                    class="bubble-menu-item"
                    @click="editor.chain().focus().toggleUnderline().run()"
                    :class="{ 'is-active': editor.isActive('underline') }"
                >
                    <div class="material-icons">format_underlined</div>
                </div>
                <div class="bubble-menu-item ai-item" @click="$feature.beforeUseChatCopilot(aiTools)">
                    <div class="material-icons">auto_awesome</div>
                </div>
            </div>
        </bubble-menu>
        <div class="actions">
            <div class="left-actions">
                <div
                    v-if="speechRecognition"
                    class="action-button"
                    :class="{
                        'voice-speech': speechRecognitionActive,
                    }"
                    @click="openSpeechRecognition"
                >
                    <div class="material-icons">mic</div>
                </div>
                <div class="action-button" @click="$feature.beforeUseChatCopilot(openAITools)">
                    <div class="material-icons">auto_awesome</div>
                </div>
                <div class="action-button" @click="$feature.beforeUseReadyContents(() => openReadyContents())">
                    <ReadyContentsSvg />
                </div>
            </div>
            <div class="right-actions">
                <EmojiButton :on-emoji-picked="addEmojiToMessage" v-if="emojiPicker">
                    <template #button>
                        <div class="action-button send">
                            <div class="material-icons">mood</div>
                        </div>
                    </template>
                </EmojiButton>
                <LocationShare @location="sendLocation" v-if="shareLocation && customerLocationEnabled">
                    <div class="action-button">
                        <div class="material-icons">location_on</div>
                    </div>
                </LocationShare>
                <a-tooltip :title="$t('googleDrive.uploadFromComputer')" v-if="shareAttachment && agentFileSendEnabled">
                    <div class="action-button" @click="$feature.beforeSendFileMessage(handleComputerUpload)">
                        <div class="material-icons">attachment</div>
                    </div>
                </a-tooltip>
                <a-tooltip title="Supsis Drive" v-if="shareAttachment && agentFileSendEnabled">
                    <div class="action-button" @click="$feature.beforeSendFileMessage(() => $emit('upload-file'))">
                        <SupsisDriveIcon />
                    </div>
                </a-tooltip>
                <a-tooltip title="Google Drive" v-if="shareAttachment && agentFileSendEnabled && googleDriveEnabled">
                    <div class="action-button" @click="$feature.beforeSendFileMessage(openGoogleDrivePicker)">
                        <GoogleDriveIcon :width="20" :height="18" />
                    </div>
                </a-tooltip>
                <VoiceRecorder
                    :cb="sendVoiceMessage"
                    @recording="voiceRecording = $event"
                    @state="voiceRecorderState = $event"
                    v-if="shareVoice && agentVoiceRecordEnabled && (voiceRecording || isEmpty())"
                >
                    <div class="action-button primary" :class="{ send: voiceRecorderState === 'preview' }">
                        <div class="material-icons">{{ getVoiceRecorderIcon() }}</div>
                    </div>
                </VoiceRecorder>

                <div
                    class="action-button primary send"
                    @click="submit"
                    v-if="!shareVoice || (!voiceRecording && !isEmpty())"
                >
                    <div class="material-icons">send</div>
                </div>
            </div>
        </div>
        <editor-content :editor="editor" :class="'editor'" ref="editor" />
    </div>
</template>
<script>
import { BubbleMenu, Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import HardBreak from "@tiptap/extension-hard-break";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { Extension } from "@tiptap/core";
import { mapState } from "vuex";
import BannedWords from "./editor-plugins/BannedWords";
import LocationShare from "../../../../LocationShare.vue";
import VoiceRecorder from "../../../../VoiceRecorder.vue";
import EmojiButton from "../../../../EmojiButton.vue";
import SpeechToTextService from "../../../../../utils/speech/SpeechToTextService";
import ReadyContentsSvg from "../../svg/ReadyContentsSvg.vue";
import SupsisDriveIcon from "../../../../svg/appSwitcherIcon/DriveIcon.vue";
import GoogleDriveIcon from "../../../../../views/Pages/Integrations/google-drive/GoogleDriveIcon.vue";
import log2 from "../../../../../utils/log2";
import _ from "lodash";
import SYSTEM_PUBSUB from "../../../../../utils/systemPubSub";

const log = log2("ChatEditor");
export default {
    name: "ChatEditor",
    components: { ReadyContentsSvg, EmojiButton, VoiceRecorder, LocationShare, EditorContent, BubbleMenu, SupsisDriveIcon, GoogleDriveIcon },
    props: {
        value: {
            type: String,
            default: null,
        },
        placeholder: {
            type: String,
            default: null,
        },
        shareVoice: {
            type: Boolean,
            default: false,
        },
        shareLocation: {
            type: Boolean,
            default: false,
        },
        shareAttachment: {
            type: Boolean,
            default: false,
        },
        isText: {
            type: Boolean,
            default: false,
        },
        autoFocus: {
            type: Boolean,
            default: true,
        },
        enabledBannedWords: {
            type: Boolean,
            default: false,
        },
        speechRecognition: {
            type: Boolean,
            default: false,
        },
        emojiPicker: {
            type: Boolean,
            default: false,
        },
        editable: {
            type: Boolean,
            default: true,
        },
        acceptedFiles: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        ...mapState({
            contents: (state) => state.content.contents.data,
            bannedWords: (state) => state.bannedWord.bannedWords.data,
            agentVoiceRecordEnabled: (state) => _.get(state, "setting.settings.agentVoiceRecordEnabled", true),
            agentFileSendEnabled: (state) => _.get(state, "setting.settings.agentFileSendEnabled", true),
            customerLocationEnabled: (state) => _.get(state, "setting.settings.customerLocationEnabled", true),
            googleDriveEnabled: (state) => state.channel.channels.some((x) => x.type === "google-drive" && x.enabled),
        }),
        tippyOptions() {
            return {
                duration: 100,
                onCreate: this.onCreate,
            };
        },
    },
    data: () => ({
        editor: null,
        tippyInstance: null,
        sendEnterEnabled: true,
        tabTrigger: false,
        voiceRecording: false,
        voiceRecorderState: "idle",
        aiToolsActive: false,
        speechRecognitionActive: false,
        speechRecognitionInstance: null,
        listeners: [],
        manuelReadyContents: false,
        showSupsisModal: false,
    }),
    methods: {
        getVoiceRecorderIcon() {
            if (this.voiceRecorderState === "recording") {
                return "stop";
            } else if (this.voiceRecorderState === "preview") {
                return "send";
            } else {
                return "mic";
            }
        },
        handleComputerUpload() {
            this.choseFile();
        },
        openGoogleDrivePicker() {
            this.$emit("open-google-drive-picker");
        },
        handleSupsisUpload() {
            this.showSupsisModal = true;
        },
        handleModalOk() {
            this.showSupsisModal = false;
        },
        handleModalCancel() {
            this.showSupsisModal = false;
        },
        choseFile() {
            let fileEl = document.createElement("input");
            fileEl.setAttribute("type", "file");
            if (this.acceptedFiles && this.acceptedFiles.length > 0) {
                fileEl.setAttribute("accept", this.acceptedFiles.join(","));
            }
            fileEl.onchange = () => {
                this.$emit("media-message", fileEl.files[0]);
            };
            fileEl.click();
        },
        isTabEnabled() {
            return this.tabTrigger;
        },
        openAITools() {
            if (this.aiToolsActive) {
                this.closeAITools();
                return;
            }
            this.aiTools();
        },
        openReadyContents(type) {
            this.manuelReadyContents = !this.manuelReadyContents;
            this.$emit("updateSuperMenuState", {
                enabled: this.manuelReadyContents,
                command: "",
                text: "",
                type, //hash , tab
            });
        },
        isEmpty() {
            return this.editor && this.editor.getText().trim().length <= 0;
        },
        setContent(val, position) {
            const from = position?.from || 0;
            const to = position?.to || this.editor.view.state.selection.to;
            if (position) {
                const newText = this.editor.state.tr.insertText(val, from, to);
                this.editor.view.dispatch(newText);
            } else {
                this.editor.commands.setContent(val);
            }
            this.editor.commands.focus("end");
            const htmlEditor = this.$refs.editor.$el;
            const scrollWidth = htmlEditor.scrollWidth;
            htmlEditor.scrollTo(0, scrollWidth);
            const text = this.isText ? this.editor.getText() : this.editor.getHTML();
            this.$emit("input", text);
        },
        focus() {
            this.editor.commands.focus("end");
        },
        blur() {
            this.editor.commands.blur();
        },
        sendLocation(location) {
            this.$emit("location", location);
        },
        onCreate(instance) {
            this.tippyInstance = instance;
        },
        hideBubbleMenu() {
            if (this.tippyInstance) this.tippyInstance.hide();
        },
        aiTools() {
            const selection = this.editor.view.state.selection;
            const selectionMode = selection.from !== selection.to;
            let { from, to } = selection;
            if (from === to) {
                from = 0;
                to = this.editor.state.doc.content.size;
            }
            const text = selectionMode ? this.editor.state.doc.textBetween(from, to, "") : this.editor.getText();
            this.$emit("ai-tools", {
                text,
                from,
                to,
                selectionMode: this.editor.view.state.selection.from !== this.editor.view.state.selection.to,
            });
            this.aiToolsActive = true;
            this.hideBubbleMenu();
        },
        closeAITools(parent = false) {
            this.aiToolsActive = false;
            if (!parent) this.$emit("ai-tools", null);
        },
        submit() {
            log("submit");
            if (this.enabledBannedWords && this.isIncludeBannedWord()) {
                this.markBannedWord();
                return;
            }
            if (this.isEmpty()) return;
            const content = this.isText ? this.editor.getText() : this.editor.getHTML();
            if (!content.replace(/<\/?(?!\/>)[^>]+>/g, "").length) return;
            this.$emit("submit", content);
            this.editor.commands.clearContent();
            this.editor.commands.insertContent("");
        },
        updateSuperMenuState() {
            this.$feature.isReadyContentsEnabled(() => {
                let trigger = this.isHashCommand();
                if (!trigger.enabled) trigger = this.isTabCommand();
                // if (!trigger.enabled) trigger = this.isSlashCommand();
                // if (!trigger.enabled) trigger = this.isVariables();
                this.sendEnterEnabled = !trigger.enabled;
                this.$emit("updateSuperMenuState", trigger);
            });
        },
        isHashCommand() {
            const content = this.editor.getText();
            //startsWith("#")
            const enabled = content.startsWith("#");
            const command = content.match(/#(\w+$)/)?.[1] || "";
            return {
                enabled,
                command,
                text: content,
                type: "canned-messages-hash",
            };
        },
        isSlashCommand() {
            const content = this.editor.getText();
            const enabled = /^\/\w/.test(content);
            const command = content.match(/^\/(\w+)/)?.[1] || "";
            const text = content.replace(/^\/\w+/, "").trim();
            return {
                enabled,
                command,
                text,
                type: "commands",
            };
        },
        isVariables() {
            const content = this.editor.getText();
            const enabled = /^@\S*$/g.test(content);
            const command = content.match(/^@(\w+$)/)?.[1] || "";
            return {
                enabled,
                command,
                text: content,
                type: "variables",
            };
        },
        isTabCommand() {
            const content = this.editor.getText().trim();
            const command = `${content}`.split(/\s/g).pop();
            let enabled = this.contents.find((content) => content.type === "TAB" && content.letter === command);
            if (!enabled) {
                enabled = this.contents.find(
                    (content) =>
                        content.type === "TAB" &&
                        content.letter === command.slice(0, -1) &&
                        content.number === command.slice(-1)
                );
            }

            if (!enabled) this.tabTrigger = false;
            return {
                enabled: !!(this.tabTrigger && enabled),
                command,
                text: content,
                type: "canned-messages-tab",
            };
        },
        catchEvents(e) {
            if (e.altKey || e.ctrlKey) {
                const altCommand = this.contents.find((content) => content.type === "ALT" && content.letter === e.key);
                if (altCommand) {
                    const content = altCommand.languageContent[this.$i18n.locale]?.content || "";
                    this.setContent(content);
                    e.preventDefault();
                }
            } else if (e.key === "Tab") {
                const tabCommand = this.contents.find((content) => content.type === "TAB");
                if (tabCommand) {
                    this.tabTrigger = true;
                    this.updateSuperMenuState();
                    e.preventDefault();
                }
            }
        },
        generateBannedWordRegexp(deep) {
            const bannedWords = this.bannedWords
                .map((word) => word.content.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&"))
                .join("|");
            return new RegExp(`(${bannedWords})${deep ? "(?![^<]*<\\/banned-word>)" : ""}`, "gi");
        },
        isIncludeBannedWord() {
            const doc = this.editor.getHTML();
            const bannedWordsRegExp = this.generateBannedWordRegexp();
            return this.bannedWords.length > 0 && bannedWordsRegExp.test(doc);
        },
        markBannedWord() {
            const doc = this.editor.getHTML();
            const bannedWordsRegExp = this.generateBannedWordRegexp(true);
            this.editor.commands.setContent(
                doc.replace(bannedWordsRegExp, (word) => {
                    return `<banned-word word="${word}">${word}</banned-word>`;
                })
            );
        },
        sendVoiceMessage(blob) {
            this.$emit("voice-message", blob);
        },
        addEmojiToMessage(emoji) {
            this.editor.commands.insertContent(emoji);
        },
        stopSpeechRecognition() {
            if (this.speechRecognitionActive) {
                this.speechRecognitionInstance.stop();
                this.speechRecognitionInstance = null;
                this.speechRecognitionActive = false;
            }
        },
        openSpeechRecognition() {
            if (this.speechRecognitionActive) {
                this.stopSpeechRecognition();
                return;
            }
            this.speechRecognitionActive = !this.speechRecognitionActive;
            this.speechRecognitionInstance = new SpeechToTextService(this.speechRecognitionContinousMode);
            const textListener = (e) => {
                this.editor.commands.setContent(e.detail);
            };

            const endListener = (e) => {
                const message = e.detail || "";
                if (message.length) {
                    this.editor.commands.setContent(e.detail);
                    this.stopSpeechRecognition();
                }
            };

            const errorListener = () => {
                this.stopSpeechRecognition();
            };

            this.speechRecognitionInstance.addEventListener("text", textListener);
            this.speechRecognitionInstance.addEventListener("end", endListener);
            this.speechRecognitionInstance.addEventListener("error", errorListener);
        },
        keyboardShortCutsListeners() {
            const keydownListener = (e) => {
                const isMeta = e.ctrlKey || e.metaKey;
                if (isMeta && e.key === ",") {
                    this.$feature.isChatCopilotEnabled(() => {
                        if (this.aiToolsActive) {
                            this.closeAITools();
                            return;
                        }
                        this.aiTools();
                        this.editor.commands.blur();
                    });
                } else if (isMeta && e.key === '"') {
                    this.openSpeechRecognition();
                }
            };
            const keyupListener = (e) => {
                if (this.speechRecognitionActive && e.key === "Meta") {
                    this.stopSpeechRecognition();
                }
            };

            document.addEventListener("keydown", keydownListener);
            document.addEventListener("keyup", keyupListener);
            this.listeners.push(() => {
                document.removeEventListener("keydown", keydownListener);
                document.removeEventListener("keyup", keyupListener);
            });
        },
    },
    mounted() {
        const SubmitEnter = Extension.create({
            addKeyboardShortcuts: () => ({
                Enter: ({ event }) => {
                    if (this.sendEnterEnabled && !this.aiToolsActive) {
                        event?.preventDefault();
                        event?.stopPropagation();
                        this.submit();
                    }
                    return true;
                },
            }),
        });
        this.editor = new Editor({
            extensions: [
                BannedWords,
                SubmitEnter,
                Underline,
                Link,
                StarterKit.configure({
                    bold: !this.isText,
                    code: false,
                    italic: !this.isText,
                    heading: false,
                    codeBlock: false,
                    blockquote: false,
                    strike: !this.isText,
                    orderedList: false,
                }),
                Placeholder.configure({
                    placeholder: this.placeholder || "Type here",
                    showOnlyWhenEditable: true,
                    includeChildren: false,
                }),
                HardBreak.configure({
                    HTMLAttributes: {
                        class: "break",
                    },
                }),
            ],
            content: this.value,
            onUpdate: () => {
                const text = this.isText ? this.editor.getText() : this.editor.getHTML();
                this.updateSuperMenuState();
                this.$emit("input", text);
            },
            enablePasteRules: false,
            enableInputRules: false,
            injectCSS: true,
            editable: this.editable,
        });
        if (this.autoFocus) this.editor.commands.focus("end");
        this.keyboardShortCutsListeners();
        this.systemPubSubListener = SYSTEM_PUBSUB.stream.subscribe((data) => {
            if (
                [
                    SYSTEM_PUBSUB.MESSAGE_TYPES.SUPER_MENU_ACTION_SELECTED,
                    SYSTEM_PUBSUB.MESSAGE_TYPES.SUPER_MENU_CLOSED,
                ].includes(data.type)
            ) {
                log("SYSTEM_PUBSUB.MESSAGE_TYPES.SUPER_MENU_ACTION_SELECTED", data);
                this.sendEnterEnabled = true;
            }
        });
        this.listeners.push(() => {
            this.systemPubSubListener.unsubscribe();
        });
    },
    watch: {
        value: {
            handler(newVal) {
                if (this.editor && newVal !== this.editor.getHTML()) {
                    const { state, view } = this.editor;
                    const { from, to } = state.selection;
                    this.editor.commands.setContent(newVal);
                    view.dispatch(
                        view.state.tr.setSelection(view.state.selection.constructor.near(view.state.doc.resolve(from)))
                    );
                    this.updateSuperMenuState();
                }
            },
        },
        editable: {
            handler(newVal) {
                if (this.editor) {
                    this.editor.setOptions({ editable: newVal });
                    if (newVal) this.editor.commands.focus("end");
                }
            },
        },
    },
    beforeDestroy() {
        this.listeners.forEach((cb) => cb());
        this.editor?.destroy();
    },
};
</script>
<style lang="scss">
$maxHeight: 200px;
$minHeight: 120px;

@keyframes voiceSpeakAnimation {
    0% {
        box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.3);
    }
    25% {
        box-shadow: 0px 0px 0px 5px rgba(0, 0, 0, 0.2);
    }
    100% {
        box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.3);
    }
}

.chat-editor {
    min-height: #{$minHeight};
    max-height: #{$maxHeight};
    position: relative;
    display: flex;
    align-items: center;
    padding: 20px;
    padding-bottom: 50px;
    background: #fafbfd;

    .editor {
        width: 100%;
        align-self: flex-start;
        max-height: calc(#{$maxHeight} - 20px * 2 - 30px);
        overflow: hidden;
        overflow-y: auto;

        &::-webkit-scrollbar {
            display: none;
            width: 0;
        }

        .ProseMirror {
            max-height: calc(#{$maxHeight} - 20px * 2 - 30px);
            min-height: calc(#{$minHeight} - 20px * 2 - 30px);
        }
    }

    .bubble-menu {
        padding: 2px;
        border-radius: 6px;
        background: var(--color-bg-neutral-default);
        border: 1.4px solid var(--border-neutral-default);
        color: var(--primary-default-button-color);
        display: flex;
        align-items: center;
        gap: 4px;

        &-item {
            padding: 4px;
            line-height: 0;
            border-radius: 6px;
            cursor: pointer;

            &.ai-item {
                background: linear-gradient(90deg, rgb(71 73 157) 0%, rgb(166 41 141) 50%, rgb(182 87 129) 100%);

                .material-icons {
                    color: white;
                }
            }

            &.is-active {
                background: var(--primary-default-button-color);

                .material-icons {
                    color: white;
                }
            }
        }
    }

    .actions {
        position: absolute;
        right: 10px;
        bottom: 10px;
        display: flex;
        gap: 5px;
        align-items: center;
        width: calc(100% - 20px);
        justify-content: space-between;

        .action-button {
            background: var(--color-bg-neutral-default);
            border: 1px solid var(--border-neutral-default);
            border-radius: 40px;
            line-height: 0;
            cursor: pointer;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            user-select: none;

            &.voice-speech {
                animation-name: voiceSpeakAnimation;
                animation-duration: 1s;
                animation-iteration-count: infinite;
            }

            &.primary {
                background: var(--primary-default-button-color) !important;
                border: 1px solid var(--primary-default-button-color) !important;

                .material-icons {
                    color: white !important;
                }

                svg > g > path {
                    fill: white !important;
                }
            }

            &.send {
                .material-icons {
                    font-size: 18px;
                }
            }

            .material-icons {
                color: var(--primary-default-button-color);
            }

            svg > g > path {
                fill: var(--primary-default-button-color) !important;
            }
        }

        .left-actions {
            display: flex;
            gap: 5px;
            align-items: center;
        }

        .right-actions {
            display: flex;
            gap: 5px;
            align-items: center;
        }
    }

    p {
        margin: 0 !important;
    }

}
</style>
